import type { Logger } from "@expert/logging";

import { DisplayCue } from "@expert/contentful";
import { trackFullStoryEvent } from "@expert/monitoring";
import pDebounce from "p-debounce";

import { getRootDispatcher } from "@/analytics";
import { type HoneycombEligibleProducts, sendToHoneycomb } from "@/analytics/sendToHoneycomb";
import { useDetectedCuesStore } from "@/sales-tools/detected-cues/stores/DetectedCuesStore";

import { type EligibleProduct, useProductOfferStore } from "../stores";
import { type EligibilityRequest, EligibilityStatus } from "../types";
import { getSalesApiClient } from "./api";

const ELIGIBILITY_DEBOUNCE_TIME = 500;
let lastEligibilityCheckTime = 0;

export type MdnLookupResult = {
    hasReachedMaxMdnLookups: boolean;
    previousHashedMdnLookups: string[];
};

export type EligibilityMeta = {
    mdnLookupResult: MdnLookupResult;
};

export type CheckEligibilityResult = {
    productConfigs: EligibleProduct[];
    meta: EligibilityMeta;
    displayCues?: DisplayCue[];
};

// Store the logger for use in the debounced function
let currentLogger: Logger | null = null;

export const getEligibility = async (
    logger: Logger,
    eligibility: EligibilityRequest,
): Promise<CheckEligibilityResult> => {
    currentLogger = logger;

    const now = performance.now();
    const timeSinceLastCheck = now - lastEligibilityCheckTime;
    logger.info(
        { timeSinceLastCheck, isPrecheck: eligibility.eligibilityAttributes?.isPrecheck },
        "Eligibility check triggered",
    );

    if (lastEligibilityCheckTime > 0 && timeSinceLastCheck < ELIGIBILITY_DEBOUNCE_TIME) {
        logger.warn(
            {
                timeSinceLastCheck,
                isPrecheck: eligibility.eligibilityAttributes?.isPrecheck,
            },
            `Eligibility check triggered more than once within ${ELIGIBILITY_DEBOUNCE_TIME}ms. Debouncing...`,
        );
    }
    lastEligibilityCheckTime = now;

    return debouncedPerformEligibilityCheck(eligibility);
};

const debouncedPerformEligibilityCheck = pDebounce(
    async (eligibility: EligibilityRequest): Promise<CheckEligibilityResult> => {
        // currentLogger is always set by getEligibility before this is called
        const logger = currentLogger!.child({ module: "eligibility" });

        try {
            const salesApi = getSalesApiClient();

            const salesApiRes = await salesApi
                .post("v2/eligibility", {
                    json: eligibility,
                    retry: 0, // retries already done via @tanstack/react-query
                })
                .json<CheckEligibilityResult>();

            return handleResponse(eligibility, salesApiRes);
        } catch (error: unknown) {
            logger.warn({ error }, "eligibility API call failed");
            throw error;
        }
    },
    ELIGIBILITY_DEBOUNCE_TIME,
);

const handleResponse = (
    eligibility: EligibilityRequest,
    checkEligibilityResult: CheckEligibilityResult,
): CheckEligibilityResult => {
    checkEligibilityResult.productConfigs.forEach((result) => {
        let dispatcher = getRootDispatcher().withExtra({
            productSku: result.productSku,
            productOfferId: result.id,
            eligibilityStatus: result.status,
        });

        if (eligibility.reservationSid) {
            dispatcher = dispatcher.withIdentity("ReservationSid", eligibility.reservationSid);
        }

        void dispatcher.dispatchBusinessEvent("CustomerValidated");
        void dispatcher.dispatchAction("ApiResult", "EligibilityStatus");
        trackFullStoryEvent("EligibilityStatus", {
            productSku: result.productSku,
            eligibilityStatus: result.status,
            productOfferId: result.id,
            reservationSid: eligibility.reservationSid ?? "unknown",
            sessionId: eligibility.sessionId,
            partner: eligibility.partner,
            hasAddress: !!eligibility.address,
        });
    });

    useProductOfferStore
        .getState()
        .updateProducts(
            eligibility.sessionId,
            checkEligibilityResult.productConfigs,
            checkEligibilityResult.displayCues,
        );

    const hasExistingCues = !!useDetectedCuesStore.getState().getDisplayCues(eligibility.sessionId).length;
    if (!hasExistingCues && checkEligibilityResult.displayCues) {
        useDetectedCuesStore
            .getState()
            .setDefaultPartnerCues(eligibility.sessionId, checkEligibilityResult.displayCues);
    }
    const honeycombEligibility: HoneycombEligibleProducts[] = checkEligibilityResult.productConfigs
        .filter((p) => p.status === EligibilityStatus.Eligible)
        .map((p) => {
            return {
                productSku: p.productSku,
                productOfferId: p.id,
                eligibilityStatus: p.status,
                displayName: useProductOfferStore.getState().getProductInfo(eligibility.sessionId, p.productSku)
                    ?.displayName,
            };
        });
    sendToHoneycomb(eligibility.sessionId, { eligibility: honeycombEligibility });

    return checkEligibilityResult;
};
